// src/components/system/AppGate.jsx
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../utils/supabase";

const MAINTENANCE_FLAG_KEY = "global_lock_lab";
const FORCE_RELOAD_FLAG_KEY = "global_force_reload_lab";

// =======================
// CONTEXT
// =======================
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// =======================
// HELPERS
// =======================
function sameUser(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.id === b.id;
}

function sameProfile(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.role === b.role && a.full_name === b.full_name && a.client_id === b.client_id;
}

// =======================
// APP GATE
// =======================
export default function AppGate({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [maintenanceEnabled, setMaintenanceEnabled] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);

  // Solo bloquea render en el primer arranque
  const [initializing, setInitializing] = useState(true);

  // Background flags (NO desmontan UI)
  const [refreshingAuth, setRefreshingAuth] = useState(false);
  const [refreshingProfile, setRefreshingProfile] = useState(false);

  const mountedRef = useRef(true);
  const lastUserRef = useRef(null);
  const lastProfileRef = useRef(null);
  const profileFetchInFlightRef = useRef(null);

  const role = profile?.role ?? null;
  const authReady = !!(user && role);
  const isAdminLockRoute =
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/_admin/lock");

  // ✅ IMPORTANTE: useMemo SIEMPRE se ejecuta (nada de hooks después de returns)
  const ctxValue = useMemo(
    () => ({
      user,
      profile,
      role,
      permissions,
      hasPermission: (permission) =>
        role !== "administrative_assistant" ||
        permissions.includes(permission),
      authReady,
      // útil si querés mostrar un spinner discreto sin desmontar
      loading: initializing || refreshingAuth || refreshingProfile,
      refreshingAuth,
      refreshingProfile,
    }),
    [user, profile, role, permissions, authReady, initializing, refreshingAuth, refreshingProfile]
  );

  const fetchProfile = async (userId) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, role, full_name, client_id")
      .eq("id", userId)
      .maybeSingle(); // evita 406 si no hay fila

    if (error) throw error;
    return data ?? null;
  };

  const fetchPermissions = async (userId, profileRole) => {
    if (!userId || profileRole !== "administrative_assistant") return [];

    const { data, error } = await supabase
      .from("user_permissions")
      .select("permission")
      .eq("user_id", userId)
      .eq("enabled", true);

    if (error) throw error;
    return (data || []).map((row) => row.permission).filter(Boolean);
  };

  const fetchMaintenanceFlag = async ({ silent = false } = {}) => {
    if (!silent) setMaintenanceLoading(true);
    try {
      const { data, error } = await supabase
        .from("app_flags")
        .select("enabled, message")
        .eq("key", MAINTENANCE_FLAG_KEY)
        .maybeSingle();

      if (error) throw error;

      if (!mountedRef.current) return;
      setMaintenanceEnabled(!!data?.enabled);
      setMaintenanceMessage(data?.message || "");
    } catch (e) {
      console.error("Error cargando maintenance flag:", e);
    } finally {
      if (mountedRef.current) setMaintenanceLoading(false);
    }
  };

  const safeSetUser = (nextUser) => {
    const prev = lastUserRef.current;
    if (sameUser(prev, nextUser)) return;
    lastUserRef.current = nextUser;
    setUser(nextUser);
  };

  const safeSetProfile = (nextProfile) => {
    const prev = lastProfileRef.current;
    if (sameProfile(prev, nextProfile)) return;
    lastProfileRef.current = nextProfile;
    setProfile(nextProfile);
  };

  const ensureProfile = async ({ userId, silent }) => {
    if (!userId) {
      safeSetProfile(null);
      setPermissions([]);
      return;
    }

    if (profileFetchInFlightRef.current?.userId === userId) {
      return profileFetchInFlightRef.current.promise;
    }

    if (silent) setRefreshingProfile(true);

    const promise = (async () => {
      try {
        const p = await fetchProfile(userId);
        if (!mountedRef.current) return;
        safeSetProfile(p);
        try {
          const nextPermissions = await fetchPermissions(userId, p?.role);
          if (mountedRef.current) setPermissions(nextPermissions);
        } catch (permissionError) {
          console.error("Error cargando permisos individuales:", permissionError);
          if (mountedRef.current) setPermissions([]);
        }
      } catch (e) {
        console.error("Error cargando profile:", e);
        // No limpiamos profile en refresh para no provocar resets de UI
      } finally {
        if (mountedRef.current) setRefreshingProfile(false);
        profileFetchInFlightRef.current = null;
      }
    })();

    profileFetchInFlightRef.current = { userId, promise };
    return promise;
  };

  const applySession = async ({ session, silent }) => {
    if (!silent) setInitializing(true);
    else setRefreshingAuth(true);

    try {
      const currentSession =
        session ?? (await supabase.auth.getSession()).data.session;

      if (!mountedRef.current) return;

      const nextUser = currentSession?.user ?? null;

      if (!nextUser) {
        safeSetUser(null);
        safeSetProfile(null);
        setPermissions([]);
        return;
      }

      const prevUserId = lastUserRef.current?.id ?? null;

      safeSetUser(nextUser);

      const needProfile =
        !lastProfileRef.current?.role || prevUserId !== nextUser.id || !silent;

      if (needProfile) {
        await ensureProfile({ userId: nextUser.id, silent: true });
      }
    } catch (e) {
      console.error("Error obteniendo sesión:", e);
    } finally {
      if (!mountedRef.current) return;
      setInitializing(false);
      setRefreshingAuth(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;

    // Primer arranque (bloquea)
    applySession({ silent: false });

    // Cambios auth (background)
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("Auth event:", event);

      if (event === "SIGNED_OUT" || !session?.user) {
        safeSetUser(null);
        safeSetProfile(null);
        setPermissions([]);
        setInitializing(false);
        setRefreshingAuth(false);
        setRefreshingProfile(false);
        return;
      }

      applySession({ session, silent: true });
    });

    return () => {
      mountedRef.current = false;
      authListener?.subscription?.unsubscribe?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Los cambios que haga un administrador en las casillas se reflejan en la
  // sesión del auxiliar sin necesidad de volver a iniciar sesión.
  useEffect(() => {
    if (!user?.id) return undefined;

    const reloadPermissions = async () => {
      try {
        const next = await fetchPermissions(user.id, lastProfileRef.current?.role);
        if (mountedRef.current) setPermissions(next);
      } catch (e) {
        console.error("Error actualizando permisos individuales:", e);
      }
    };

    const channel = supabase
      .channel(`user_permissions_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_permissions",
          filter: `user_id=eq.${user.id}`,
        },
        reloadPermissions
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  // Escucha force_reload — cuando enabled pasa a true, recarga la página
  useEffect(() => {
    if (isAdminLockRoute) return; // el admin no se recarga a sí mismo
    const channel = supabase
      .channel("app_flags_force_reload")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "app_flags",
          filter: `key=eq.${FORCE_RELOAD_FLAG_KEY}`,
        },
        (payload) => {
          if (payload?.new?.enabled === true) {
            window.location.reload();
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMaintenanceFlag({ silent: false });
    const channel = supabase
      .channel("app_flags_global_lock_lab")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "app_flags",
          filter: `key=eq.${MAINTENANCE_FLAG_KEY}`,
        },
        (payload) => {
          if (!mountedRef.current) return;
          const row = payload?.new ?? payload?.old ?? null;
          setMaintenanceEnabled(!!row?.enabled);
          setMaintenanceMessage(row?.message || "");
          setMaintenanceLoading(false);
        }
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          // Fallback puntual si falla realtime
          fetchMaintenanceFlag({ silent: true });
        }
      });

    return () => {
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, []);

  // ✅ Return condicional DESPUÉS de haber ejecutado TODOS los hooks
  if (initializing || (!isAdminLockRoute && maintenanceLoading)) {
    return (
      <div style={{ padding: 40, color: "#fff" }}>
        Cargando aplicación…
      </div>
    );
  }

  if (!isAdminLockRoute && maintenanceEnabled) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24, color: "#fff" }}>
        <div style={{ maxWidth: 680, width: "100%", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, padding: 20, background: "rgba(0,0,0,0.35)" }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700 }}>Aplicación en mantenimiento</h1>
          <p style={{ marginTop: 12, opacity: 0.9 }}>
            {maintenanceMessage || "Estamos realizando tareas de mantenimiento. Intentá nuevamente en unos minutos."}
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={ctxValue}>{children}</AuthContext.Provider>;
}
